import React, { useState, useEffect, useRef } from 'react';
import { getTemplates, createTemplate, deleteTemplate } from '../api';
import TagsInput from '../components/TagsInput';
import toast from 'react-hot-toast';
import { FileText, Plus, Trash2, X, Paperclip, Eye, Copy } from 'lucide-react';

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [show, setShow] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ name:'', subject:'', body:'', tags:[] });
  const [files, setFiles] = useState([]);
  const fileRef = useRef();

  useEffect(() => { load(); }, []);
  const load = async () => { try { setTemplates((await getTemplates()).data); } catch(e){} };

  const handleCreate = async () => {
    if (!form.name||!form.subject||!form.body) return toast.error('Name, subject, and body required');
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('subject', form.subject);
    fd.append('body', form.body);
    fd.append('tags', JSON.stringify(form.tags));
    files.forEach(f => fd.append('attachments', f));
    try {
      await createTemplate(fd);
      toast.success('Template created!');
      setShow(false); setForm({ name:'',subject:'',body:'',tags:[] }); setFiles([]); load();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await deleteTemplate(id); toast.success('Deleted'); load(); } catch(e){ toast.error('Failed'); }
  };

  const dupBody = (tpl) => {
    setForm({ name: tpl.name + ' (copy)', subject: tpl.subject, body: tpl.body, tags: [...tpl.tags] });
    setShow(true);
  };

  return (<>
    <div className="pg-hdr">
      <h2>Email Templates</h2>
      <p>Create unlimited templates with attachments. Use {'{{name}}'}, {'{{company}}'}, {'{{email}}'} variables.</p>
      <div className="pg-acts"><button className="btn btn-p" onClick={()=>setShow(true)}><Plus size={15}/> New Template</button></div>
    </div>
    <div className="pg-body">
      {templates.length===0 ? <div className="card"><div className="empty"><FileText size={44}/><h3>No templates</h3><p>Create your first email template</p></div></div> : (
        <div style={{display:'grid',gap:10}}>{templates.map(t=>(
          <div className="card" key={t._id} style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
            <div style={{flex:1}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                <span style={{fontWeight:600,fontSize:14}}>{t.name}</span>
                {t.tags.map((tag,i)=><span className="tag" key={i}>{tag}</span>)}
              </div>
              <div style={{fontSize:12,color:'var(--txtm)',marginBottom:4}}>Subject: {t.subject}</div>
              {t.attachments?.length>0 && <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>{t.attachments.map((a,i)=><span key={i} style={{fontSize:11,color:'var(--txt2)',display:'flex',alignItems:'center',gap:3}}><Paperclip size={11}/>{a.originalName} <span style={{color:'var(--txtm)'}}>({(a.size/1024).toFixed(0)}KB)</span></span>)}</div>}
            </div>
            <div style={{display:'flex',gap:5}}>
              <button className="btn btn-g btn-sm" onClick={()=>setPreview(t)} title="Preview"><Eye size={13}/></button>
              <button className="btn btn-g btn-sm" onClick={()=>dupBody(t)} title="Duplicate"><Copy size={13}/></button>
              <button className="btn btn-d btn-sm" onClick={()=>handleDelete(t._id)}><Trash2 size={13}/></button>
            </div>
          </div>
        ))}</div>
      )}
    </div>

    {show && <div className="modal-bg" onClick={()=>setShow(false)}><div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr"><h3>Create Template</h3><button className="btn btn-g btn-sm" onClick={()=>setShow(false)}><X size={15}/></button></div>
      <div className="modal-body">
        <div className="fr2">
          <div className="fg"><label className="lbl">Template Name *</label><input className="inp" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Welcome Email"/></div>
          <div className="fg"><label className="lbl">Tags</label><TagsInput tags={form.tags} onChange={tags=>setForm({...form,tags})}/></div>
        </div>
        <div className="fg">
          <label className="lbl">Subject *</label>
          <input className="inp" value={form.subject} onChange={e=>setForm({...form,subject:e.target.value})} placeholder="Hi {{name}}, quick question about {{company}}"/>
          <div style={{fontSize:10.5,color:'var(--txtm)',marginTop:3}}>Variables: {'{{name}}'}, {'{{company}}'}, {'{{email}}'}</div>
        </div>
        <div className="fg">
          <label className="lbl">Body (HTML) *</label>
          <textarea className="inp tarea" value={form.body} onChange={e=>setForm({...form,body:e.target.value})}
            style={{minHeight:200,fontFamily:'var(--mono)',fontSize:12}}
            placeholder={'<p>Hi {{name}},</p>\n<p>I noticed {{company}} is doing great work...</p>\n<p>Would love to connect!</p>\n<p>Best regards,<br/>Your Name</p>'}/>
        </div>
        <div className="fg">
          <label className="lbl">Attachments</label>
          <input type="file" ref={fileRef} multiple style={{display:'none'}} onChange={e=>setFiles([...files,...Array.from(e.target.files)])}/>
          <div className="upload-zone" onClick={()=>fileRef.current?.click()}>
            <Paperclip size={28} style={{color:'var(--txtm)',marginBottom:6}}/>
            <p style={{fontSize:12.5,color:'var(--txt2)'}}>Click to add attachments (PDF, images, docs — max 25MB each)</p>
          </div>
          {files.length>0 && <div style={{marginTop:8,display:'flex',flexWrap:'wrap',gap:6}}>{files.map((f,i)=>(
            <span key={i} style={{fontSize:11,display:'flex',alignItems:'center',gap:4,padding:'3px 8px',background:'var(--bg3)',borderRadius:8,color:'var(--txt2)'}}>
              <Paperclip size={10}/> {f.name} ({(f.size/1024).toFixed(0)}KB)
              <X size={10} style={{cursor:'pointer',opacity:.6}} onClick={()=>setFiles(files.filter((_,j)=>j!==i))}/>
            </span>
          ))}</div>}
        </div>
      </div>
      <div className="modal-ft">
        <button className="btn btn-s" onClick={()=>setShow(false)}>Cancel</button>
        <button className="btn btn-p" onClick={handleCreate}><Plus size={15}/> Create Template</button>
      </div>
    </div></div>}

    {preview && <div className="modal-bg" onClick={()=>setPreview(null)}><div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr"><h3>Preview: {preview.name}</h3><button className="btn btn-g btn-sm" onClick={()=>setPreview(null)}><X size={15}/></button></div>
      <div className="modal-body">
        <div style={{marginBottom:12}}><span className="lbl">Subject</span><p style={{fontSize:13}}>{preview.subject}</p></div>
        <div style={{marginBottom:12}}><span className="lbl">Body</span>
          <div style={{background:'#fff',color:'#333',padding:20,borderRadius:'var(--rs)',fontSize:14,lineHeight:1.7}} dangerouslySetInnerHTML={{__html:preview.body}}/>
        </div>
        {preview.attachments?.length>0 && <div><span className="lbl">Attachments</span>{preview.attachments.map((a,i)=><div key={i} style={{fontSize:12,color:'var(--txt2)',display:'flex',alignItems:'center',gap:4}}><Paperclip size={12}/>{a.originalName}</div>)}</div>}
      </div>
    </div></div>}
  </>);
}
